import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, CircularProgress, Alert,
  InputAdornment, Select, MenuItem, FormControl, InputLabel, Stack,
  Menu, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { getDecisionTrees, createDecisionTree, deleteDecisionTree } from '../services/api';
import { useApiCall } from '../hooks/useApiCall';
import { useAuth } from '../hooks/useAuth';

const emptyForm = { title: '', specialty_tag: '' };

// updated_at viene naive-UTC del backend — agregar la 'Z' evita que el
// navegador lo interprete como hora local.
const fmtUpdatedAt = (d) => new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d).toLocaleDateString('es-AR');

const Algoritmos = () => {
  const navigate = useNavigate();
  const apiCall = useApiCall();
  const { isInvitado } = useAuth();
  const canManage = !isInvitado;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTree, setMenuTree] = useState(null);
  const openMenu = (e, t) => { setMenuAnchor(e.currentTarget); setMenuTree(t); };
  const closeMenu = () => { setMenuAnchor(null); setMenuTree(null); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await getDecisionTrees();
      setTrees(r.data || []);
    } catch {
      setTrees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const specialties = useMemo(
    () => Array.from(new Set(trees.map((t) => t.specialty_tag).filter(Boolean))).sort(),
    [trees],
  );

  const filteredTrees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return trees.filter((t) => {
      if (q && !t.title.toLowerCase().includes(q)) return false;
      if (specialtyFilter && t.specialty_tag !== specialtyFilter) return false;
      if (statusFilter === 'published' && !t.is_published) return false;
      if (statusFilter === 'draft' && t.is_published) return false;
      return true;
    });
  }, [trees, search, specialtyFilter, statusFilter]);

  const openCreate = () => {
    setForm(emptyForm);
    setError('');
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { setError('El título es obligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      const r = await createDecisionTree({ title: form.title, specialty_tag: form.specialty_tag || null });
      setOpen(false);
      navigate(`/algoritmos/${r.data.id}/editar`);
    } catch (err) {
      setError(err.userMessage || 'Error al crear el algoritmo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tree) => {
    closeMenu();
    if (!window.confirm(`¿Eliminar el algoritmo "${tree.title}"? Se perderán todos sus nodos e imágenes.`)) return;
    try {
      await apiCall(deleteDecisionTree(tree.id), 'Error al eliminar');
      await load();
    } catch {}
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Algoritmos</Typography>
          <Typography variant="body2" color="text.secondary">
            Guías clínicas interactivas — árboles de decisión para guiar el diagnóstico
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AccountTreeIcon />} onClick={openCreate}>
            Nuevo algoritmo
          </Button>
        )}
      </Box>

      <Stack component={Paper} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 1.5, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por título..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Especialidad</InputLabel>
          <Select
            label="Especialidad"
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {specialties.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="published">Publicado</MenuItem>
            <MenuItem value="draft">Borrador</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : filteredTrees.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {trees.length === 0 ? 'Todavía no creaste ningún algoritmo' : 'Ningún algoritmo coincide con los filtros'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filteredTrees.map((t) => (
            <Paper
              key={t.id} sx={{ p: 1.5, cursor: 'pointer' }}
              onClick={() => navigate(t.is_published ? `/algoritmos/${t.id}` : `/algoritmos/${t.id}/editar`)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{t.title}</Typography>
                    <Chip
                      label={t.is_published ? 'Publicado' : 'Borrador'}
                      color={t.is_published ? 'success' : 'default'}
                      size="small" sx={{ height: 20, fontSize: '0.68rem' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t.specialty_tag && `${t.specialty_tag} · `}Actualizado {fmtUpdatedAt(t.updated_at)}
                  </Typography>
                </Box>
                {canManage && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openMenu(e, t); }} sx={{ flexShrink: 0 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Especialidad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Actualizado</TableCell>
                {canManage && <TableCell align="right" sx={{ width: 60 }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrees.map((t) => (
                <TableRow
                  key={t.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(canManage && !t.is_published ? `/algoritmos/${t.id}/editar` : `/algoritmos/${t.id}`)}
                >
                  <TableCell>{t.title}</TableCell>
                  <TableCell>{t.specialty_tag || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.is_published ? 'Publicado' : 'Borrador'}
                      color={t.is_published ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{fmtUpdatedAt(t.updated_at)}</TableCell>
                  {canManage && (
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Acciones">
                        <IconButton size="small" onClick={(e) => openMenu(e, t)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Menú de acciones */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => { closeMenu(); navigate(`/algoritmos/${menuTree.id}/editar`); }}>Editar</MenuItem>
        <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDelete(menuTree)}>Eliminar</MenuItem>
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo algoritmo</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Título *"
            fullWidth
            autoFocus
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Ej: Urgencias en Ginecología — Ecografía transvaginal de guardia"
            error={!form.title.trim() && !!error}
          />
          <TextField
            label="Especialidad (opcional)"
            fullWidth
            value={form.specialty_tag}
            onChange={(e) => setForm((p) => ({ ...p, specialty_tag: e.target.value }))}
            placeholder="Ej: Ginecología"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Crear y editar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Algoritmos;
